import { useCallback } from 'react';
import { haversineDistance } from '../utils/geo';
/**
 * Format a distance in meters to a human-readable string in miles.
 * Under 0.1 mi: "300 ft", under 10 mi: "2.3 mi", over 10 mi: "1,225 mi"
 */
function formatDistance(meters) {
    const feet = meters * 3.28084;
    const miles = meters / 1609.344;
    if (miles < 0.1) {
        return `${Math.round(feet).toLocaleString()} ft`;
    }
    if (miles < 10) {
        return `${miles.toFixed(1)} mi`;
    }
    return `${Math.round(miles).toLocaleString()} mi`;
}
/**
 * Hook that provides a client-side geofence position check.
 * Pure math — no API calls. Wraps the Haversine utility with
 * formatted error messages.
 */
export function useGeofenceCheck(geofenceConfig) {
    const checkPosition = useCallback((latitude, longitude) => {
        // No geofence configured — always allow
        if (!geofenceConfig || !geofenceConfig.enabled) {
            return { allowed: true };
        }
        // GPS not provided but geofencing is enabled
        if (latitude == null || longitude == null) {
            return {
                allowed: false,
                errorCode: 'GEOFENCE_GPS_REQUIRED',
                message: 'Please enable location services to perform this action.',
            };
        }
        const distance = haversineDistance(latitude, longitude, geofenceConfig.latitude, geofenceConfig.longitude);
        if (distance <= geofenceConfig.radiusMeters) {
            return { allowed: true, distanceMeters: Math.round(distance) };
        }
        const excess = distance - geofenceConfig.radiusMeters;
        return {
            allowed: false,
            distanceMeters: Math.round(distance),
            excessMeters: Math.round(excess),
            errorCode: 'GEOFENCE_VIOLATION',
            message: `You are ${formatDistance(excess)} outside the allowed area.`,
        };
    }, [geofenceConfig]);
    return { checkPosition };
}
